import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../common';
import { toast } from 'react-toastify';

const EditEmployeeModal = ({ isOpen, onClose, employee, onEmployeeUpdated }) => {
  // State for the form data itself
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', country: '', hire_date: '',
    hourly_rate: '', role_id: null, status: 'Active', notes: '', address: '',
    bank_account: '', university: '', study_program: '', smtp_profile_id: null,
    email_signature: ''
  });

  // State to hold the most up-to-date employee data fetched from the API
  const [currentEmployeeData, setCurrentEmployeeData] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [smtpProfiles, setSmtpProfiles] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state for the entire modal data fetch
  const [saving, setSaving] = useState(false); // Saving state for the submit button
  const [error, setError] = useState('');
  const API_BASE_URL = '/api';

  // Effect to fetch all necessary data when the modal opens or the employee ID changes
  useEffect(() => {
    if (isOpen && employee?.id) {
      setLoading(true);
      setError('');
      setCurrentEmployeeData(null); // Clear previous data
      setAvailableRoles([]);
      setSmtpProfiles([]);

      const fetchModalData = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('Authentication token not found.');
          }
          const config = { headers: { 'x-auth-token': token } };

          // Fetch employee details, roles, and SMTP profiles concurrently
          const [employeeRes, rolesRes, smtpRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/employees/${employee.id}`, config),
            axios.get(`${API_BASE_URL}/roles`, config),
            axios.get(`${API_BASE_URL}/settings/smtp-profiles`, config)
          ]);

          setCurrentEmployeeData(employeeRes.data); // Store the fresh employee data
          setAvailableRoles(rolesRes.data || []);
          setSmtpProfiles(smtpRes.data || []);

        } catch (err) {
          console.error('Error fetching modal data:', err);
          const errorMsg = err.response?.data?.message || 'Failed to load employee details or required data.';
          setError(errorMsg);
          toast.error(errorMsg);
        } finally {
          setLoading(false); // Stop loading regardless of success or failure
        }
      };

      fetchModalData();
    } else if (!isOpen) {
      // Reset state when modal closes
      setCurrentEmployeeData(null);
      setAvailableRoles([]);
      setSmtpProfiles([]);
      setError('');
      setLoading(false);
    }
  }, [isOpen, employee?.id]); // Re-run when modal opens or employee changes

  // Effect to populate the form *after* currentEmployeeData is fetched
  useEffect(() => {
    if (currentEmployeeData) {
      let formattedHireDate = '';
      if (currentEmployeeData.hire_date) {
        try {
          const date = new Date(currentEmployeeData.hire_date);
          if (!isNaN(date.getTime())) {
            formattedHireDate = date.toISOString().split('T')[0];
          } else {
            console.warn(`Invalid hire_date received: ${currentEmployeeData.hire_date}`);
          }
        } catch (dateError) {
          console.error(`Error parsing hire_date:`, dateError);
        }
      }

      setFormData({
        name: currentEmployeeData.name || '',
        email: currentEmployeeData.email || '',
        phone: currentEmployeeData.phone || '',
        country: currentEmployeeData.country || 'Norway', // Default country if needed
        hire_date: formattedHireDate,
        hourly_rate: currentEmployeeData.hourly_rate || '',
        role_id: currentEmployeeData.role_id || null, // Crucial: Use role_id from fetched data
        status: currentEmployeeData.status || 'Active',
        notes: currentEmployeeData.notes || '',
        address: currentEmployeeData.address || '',
        bank_account: currentEmployeeData.bank_account || '',
        university: currentEmployeeData.university || '',
        study_program: currentEmployeeData.study_program || '',
        smtp_profile_id: currentEmployeeData.smtp_profile_id || null,
        email_signature: currentEmployeeData.email_signature || ''
      });
    }
  }, [currentEmployeeData]); // Run only when fresh employee data is available

  // Effect to update hourly rate placeholder (no change needed here)
  useEffect(() => {
    const inputElement = document.getElementById('hourly_rate_edit');
    if (inputElement) {
      inputElement.placeholder = `0.00 ${
        formData.country === 'Norway' ? 'NOK' :
        formData.country === 'Sweden' ? 'SEK' :
        formData.country === 'Germany' ? 'EUR' : ''
      }`;
    }
  }, [formData.country]);
  
  // Effect to handle role changes
  useEffect(() => {
    // Get current role name
    const roleName = availableRoles.find(r => r.id === formData.role_id)?.name;
    
    // If changing from tutor to another role, reset tutor-specific fields
    if (roleName && roleName !== 'tutor' && currentEmployeeData?.role_id) {
      const previousRoleName = availableRoles.find(r => r.id === currentEmployeeData.role_id)?.name;
      if (previousRoleName === 'tutor') {
        setFormData(prev => ({
          ...prev,
          university: '',
          study_program: ''
        }));
      }
    }
  }, [formData.role_id, availableRoles, currentEmployeeData?.role_id]);


  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Handle role_id specifically to ensure it's an integer or null
    const processedValue = name === 'role_id'
      ? (value ? parseInt(value, 10) : null)
      : value;
    
    // Log role changes to help with debugging
    if (name === 'role_id') {
      const oldRoleName = availableRoles.find(r => r.id === formData.role_id)?.name;
      const newRoleName = availableRoles.find(r => r.id === parseInt(value, 10))?.name;
      console.log(`Role change: ${oldRoleName || 'none'} -> ${newRoleName || 'none'}`);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); // Use 'saving' state for submit button
    setError('');

    if (formData.role_id === null || formData.role_id === undefined) {
      setError('Please select a role for the employee.');
      setSaving(false);
      toast.error('Role is required.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': token } };

      // Get the role name for the selected role_id
      const selectedRole = availableRoles.find(r => r.id === formData.role_id);
      if (!selectedRole) {
        setError('Invalid role selected');
        setSaving(false);
        toast.error('Invalid role selection.');
        return;
      }
      
      // Prepare data, ensuring numeric/null values are correct
      const dataToSubmit = {
        ...formData,
        hourly_rate: formData.hourly_rate === '' ? null : parseFloat(formData.hourly_rate),
        hire_date: formData.hire_date === '' ? null : formData.hire_date,
        smtp_profile_id: formData.smtp_profile_id === '' || formData.smtp_profile_id === null ? null : parseInt(formData.smtp_profile_id, 10)
        // Using role_id from formData - no need to send role name anymore
      };
      
      // Remove fields that shouldn't be sent
      delete dataToSubmit.terms_signed_at;
      delete dataToSubmit.terms_version_signed;

      console.log('Submitting employee update with data:', dataToSubmit);
      const res = await axios.put(`${API_BASE_URL}/employees/${employee.id}`, dataToSubmit, config);
      console.log('Employee update response:', res.data);
      toast.success(`Employee "${res.data.name}" updated successfully.`);
      if (onEmployeeUpdated) {
        onEmployeeUpdated(res.data); // Pass updated data back
      }
      onClose(); // Close modal on success

    } catch (err) {
      console.error('Error updating employee:', err);
      const errorMsg = err.response?.data?.message || 'Failed to update employee';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Derive role name based on the currently selected role_id in the form
  const currentRoleName = formData.role_id && availableRoles.length > 0
    ? availableRoles.find(r => r.id === formData.role_id)?.name
    : null;

  // Define Tailwind classes for reuse
  const inputClass = "mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4" tabIndex="-1">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-xl font-semibold text-gray-800">Edit Employee: {currentEmployeeData?.name || employee?.name || '...'}</h3>
          <Button onClick={onClose} variant="icon" className="text-gray-500 hover:text-gray-800">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </Button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex justify-center items-center flex-grow p-6">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="edit-employee-form" className="p-6 overflow-y-auto flex-grow">
            {error && <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Left Column */}
              <div className="md:col-span-2 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label htmlFor="name_edit" className={labelClass}>Name *</label><input type="text" id="name_edit" name="name" value={formData.name} onChange={handleChange} required className={inputClass} /></div>
                  <div><label htmlFor="email_edit" className={labelClass}>Email *</label><input type="email" id="email_edit" name="email" value={formData.email} onChange={handleChange} required className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label htmlFor="phone_edit" className={labelClass}>Phone</label><input type="text" id="phone_edit" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} /></div>
                  <div>
                    <label htmlFor="role_id_edit" className={labelClass}>Role *</label>
                    <select name="role_id" id="role_id_edit" value={formData.role_id || ''} onChange={handleChange} required className={inputClass} disabled={availableRoles.length === 0}>
                      <option value="" disabled>-- Select Role --</option>
                      {availableRoles.map(role => <option key={role.id} value={role.id}>{role.name} {role.is_system_role ? '(System)' : ''}</option>)}
                    </select>
                    {availableRoles.length === 0 && !error && <p className="text-xs text-gray-500 mt-1">Loading roles...</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label htmlFor="country_edit" className={labelClass}>Country</label><input type="text" id="country_edit" name="country" value={formData.country} onChange={handleChange} className={inputClass} /></div>
                  <div><label htmlFor="hire_date_edit" className={labelClass}>Hire Date</label><input type="date" id="hire_date_edit" name="hire_date" value={formData.hire_date} onChange={handleChange} className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="hourly_rate_edit" className={labelClass}>Hourly Rate ({formData.country === 'Norway' ? 'NOK' : formData.country === 'Sweden' ? 'SEK' : formData.country === 'Germany' ? 'EUR' : ''})</label>
                    <input type="number" id="hourly_rate_edit" name="hourly_rate" value={formData.hourly_rate} onChange={handleChange} step="0.01" min="0" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="status_edit" className={labelClass}>Status</label>
                    <select id="status_edit" name="status" value={formData.status} onChange={handleChange} className={inputClass}>
                      <option value="Active">Active</option><option value="On Leave">On Leave</option><option value="Terminated">Terminated</option>
                    </select>
                  </div>
                </div>
                <div><label htmlFor="bank_account_edit" className={labelClass}>Bank Account Number</label><input type="text" id="bank_account_edit" name="bank_account" value={formData.bank_account} onChange={handleChange} className={inputClass} /></div>

                {/* Tutor-specific fields - Use derived currentRoleName */}
                {currentRoleName === 'tutor' && (
                  <div className="bg-blue-50 p-4 rounded-md border border-blue-100 mt-4">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3">Tutor Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><label htmlFor="university_edit" className={labelClass}>University</label><input type="text" id="university_edit" name="university" value={formData.university || ''} onChange={handleChange} className={inputClass} /></div>
                      <div><label htmlFor="study_program_edit" className={labelClass}>Study Program</label><input type="text" id="study_program_edit" name="study_program" value={formData.study_program || ''} onChange={handleChange} className={inputClass} /></div>
                    </div>
                    {/* Display Terms Signed Status using FRESH data */}
                    <div className="mt-4 pt-4 border-t border-blue-200">
                       <label className={`${labelClass} text-blue-800`}>Terms Signed Status</label>
                       {currentEmployeeData?.terms_signed_at ? ( // Check fresh data
                         <p className="text-sm text-gray-900 mt-1">
                           Signed Version: <span className="font-medium">{currentEmployeeData.terms_version_signed || 'N/A'}</span><br/>
                           Signed At: <span className="font-medium">{new Date(currentEmployeeData.terms_signed_at).toLocaleString()}</span>
                         </p>
                       ) : (
                         <p className="text-sm text-red-600 mt-1 font-medium">
                           Terms Not Signed
                         </p>
                       )}
                    </div>
                  </div>
                )}

                {/* Address and Notes */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label htmlFor="address_edit" className={labelClass}>Address</label><textarea id="address_edit" name="address" value={formData.address} onChange={handleChange} rows={3} className={inputClass}></textarea></div>
                  <div><label htmlFor="notes_edit" className={labelClass}>Notes</label><textarea id="notes_edit" name="notes" value={formData.notes} onChange={handleChange} rows={3} className={inputClass}></textarea></div>
                </div>
              </div>

              {/* Right Column - Email Settings */}
              <div className="md:col-span-1">
                {(currentRoleName === 'admin' || currentRoleName === 'sales' || currentRoleName === 'superuser') ? (
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200 h-full flex flex-col">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Email Settings</h4>
                    <div className="mb-4">
                      <label htmlFor="smtp_profile_id_edit" className={labelClass}>Email SMTP Profile</label>
                      <select name="smtp_profile_id" id="smtp_profile_id_edit" value={formData.smtp_profile_id || ''} onChange={handleChange} className={inputClass}>
                        <option value="">Use Default Profile</option>
                        {smtpProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name} ({profile.from_email})</option>)}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">Profile for sending emails.</p>
                    </div>
                    <div className="flex-grow flex flex-col">
                      <label htmlFor="email_signature_edit" className={labelClass}>Email Signature (HTML)</label>
                      <textarea name="email_signature" id="email_signature_edit" value={formData.email_signature || ''} onChange={handleChange} rows={6} className={`${inputClass} font-mono text-sm flex-grow`} placeholder="<b>Your Name</b><br>Title<br>Company"></textarea>
                    </div>
                    {formData.email_signature && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                        <div className="border border-gray-300 rounded-md p-2 bg-white text-sm" dangerouslySetInnerHTML={{ __html: formData.email_signature }} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-100 p-4 rounded-md border border-gray-200 text-center flex items-center justify-center h-full"><p className="text-sm text-gray-500 italic">Email settings available for Admin, Sales, Superuser roles.</p></div>
                )}
              </div>
            </div>
          </form>
        )}
        {/* Footer */}
        <div className="p-4 border-t flex justify-end space-x-3 sticky bottom-0 bg-gray-50 z-10">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} variant="primary" disabled={loading || saving} isLoading={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
